import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, Inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';

import { map, Observable } from 'rxjs';

import { formatPhoneNumber } from '../../../helpers';
import {
    ApiDataType,
    EnvironmentInterface,
    UserApiDataInterface,
    UserClientAddressInterface,
    UserClientApiDataInterface,
    UserClientFavoriteInterface,
    UserClientInterface,
    UserClientProfileInterface
} from '../../../types';

@Injectable({
    providedIn: 'root'
})
export class ClientUserService {
    user: WritableSignal<UserClientInterface | null> = signal(null);
    userExists: Signal<boolean> = computed(() => !!this.user());
    isLoggedIn: Signal<boolean> = computed(() => !!this.user() && this.userExists());
    isVerified: Signal<boolean> = computed(
        () => !!this.user() && !!this.user()?.phone && !!this.user()?.phone_verified_at
    );
    userFavorites: Signal<UserClientFavoriteInterface[]> = computed(() => this.user()?.favorites || []);
    userProfile: Signal<UserClientProfileInterface | null> = computed(() => this.user()?.profile || null);
    userDefaultAddress: Signal<UserClientAddressInterface | null> = computed(() => {
        const userAddresses = this.user()?.addresses || [];
        return userAddresses.find(address => address.is_primary) || null;
    });
    formattedPhoneNumber: Signal<string | null> = computed(() => formatPhoneNumber(this.user()?.phone || '') as string);
    storedTemporaryEmail: WritableSignal<string | null> = signal(null);
    /* eslint-disable */
    private environment: EnvironmentInterface;
    private storage: Storage;

    constructor(
        private http: HttpClient,
        @Inject('environment') environment: EnvironmentInterface,
        @Inject('storage') storage: Storage
    ) {
        this.environment = environment;
        this.storage = storage;
    }

    async initStoredTemporaryEmailFromStorage() {
        const email = await this.storage['get']('lead_email');
        this.setStoredTemporaryEmail(email);
    }

    setStoredTemporaryEmail(value: string | null) {
        this.storedTemporaryEmail.set(value);
    }

    setUser(user: UserClientInterface | null, setFavorites: boolean = true) {
        this.user.set(user);
    }

    setUserFieldValues(updatedValues: { [key: string]: string | number | object | null | undefined } = {}) {
        const user = this.user();
        if (!user) return;
        const updatedUser = {
            ...user,
            ...updatedValues
        };
        this.user.set(updatedUser);
    }

    getUserWithToken(token: string): Observable<UserApiDataInterface> {
        const header = new HttpHeaders({
            Authorization: token
        });
        return this.http
            .get<UserApiDataInterface>(`${this.environment.apiUrl}/account`, {
                headers: header
            })
            .pipe(
                map(data => {
                    console.log(data.data);
                    this.setUser(data.data);
                    return data;
                })
            );
    }

    hasUserProvidedInfo() {
        return this.isLoggedIn() || this.storedTemporaryEmail() ? true : false;
    }

    clearUser() {
        this.setUser(null);
    }

    getUpdatedUser() {
        return this.http.get<UserApiDataInterface>(`${this.environment.apiUrl}/account`).pipe(
            map(data => {
                if (data.success) {
                    this.setUser(data.data);
                } else {
                    this.clearUser();
                }
                return data;
            })
        );
    }

    createAddress(data: any): Observable<ApiDataType> {
        const address = {
            address: data.address,
            city: data.city,
            postal_code: data.postal_code,
            unit_number: data.unit,
            buzzer_number: data.buzzer,
            parking_notes: data.parking_details,
            is_parking_free: data['is_parking_free'] || '0',
            is_primary: data.is_primary
        };
        return this.http.post<ApiDataType>(`${this.environment.apiUrl}/address/create`, address);
    }

    setUserProfile(value: UserClientProfileInterface | null) {
        const user = this.user() || {};
        const newUser = user ? { ...user, profile: value } : null;
        //@ts-ignore
        this.setUser(newUser);
    }

    contact(data: any): Observable<ApiDataType> {
        return this.http.post<ApiDataType>(`${this.environment.apiUrl}/contact`, data);
    }

    updateProfile(data: any): Observable<ApiDataType> {
        return this.http.post<ApiDataType>(`${this.environment.apiUrl}/profile`, data);
    }

    updateAccount(data: any): Observable<UserClientApiDataInterface> {
        return this.http.post<UserClientApiDataInterface>(`${this.environment.apiUrl}/account`, data);
    }

    sendData(obj: any): Observable<ApiDataType> {
        const postObj = {
            request: obj.request,
            request_info: obj.request_info
        };
        return this.http.post<ApiDataType>(`${this.environment.apiUrl}`, postObj);
    }
}
